import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Filter } from 'lucide-react';

const RealisationsListPage = () => {
  const [realisations, setRealisations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('Tous');
  const navigate = useNavigate();

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

  const categories = [
    'Tous',
    'Ponts de quai',
    'Niveleurs hydrauliques',
    'Mini niveleurs',
    'Butoirs',
    'Garde-corps et barrières',
    'Installation complète'
  ];

  useEffect(() => {
    fetchRealisations();
  }, [selectedCategory]);

  const fetchRealisations = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (selectedCategory !== 'Tous') {
        params.append('category', selectedCategory);
      }
      
      const response = await fetch(`${BACKEND_URL}/api/realisations?${params}`);
      const data = await response.json();
      setRealisations(data.realisations || []);
    } catch (error) {
      console.error('Erreur chargement réalisations:', error);
      setRealisations([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Nos Réalisations - Équipements de quai | Orion Industrie</title>
        <meta name="description" content="Découvrez nos réalisations : installation de ponts de quai, niveleurs hydrauliques, butoirs et équipements de sécurité. Expertise et savoir-faire depuis 2019." />
        <link rel="canonical" href="https://orion-industrie.eu/realisations" />
        <meta property="og:title" content="Nos Réalisations - Équipements de quai | Orion Industrie" />
        <meta property="og:description" content="Découvrez nos réalisations : installation de ponts de quai, niveleurs hydrauliques, butoirs et équipements de sécurité." />
        <meta property="og:url" content="https://orion-industrie.eu/realisations" />
        <meta property="og:type" content="website" />
      </Helmet>

      <Header />
      
      <main className="bg-gray-50 min-h-screen">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-[#020d2a] to-[#0a1a3a] text-white py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <span className="text-[#FF6600] font-semibold text-sm uppercase tracking-wider">Portfolio</span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mt-3 mb-6">
                Nos Réalisations
              </h1>
              <p className="text-lg md:text-xl text-gray-300 max-w-2xl mx-auto">
                Découvrez nos chantiers récents : ponts de quai, niveleurs, butoirs et équipements de sécurité installés partout en France.
              </p>
            </div>
          </div>
        </section>

        {/* Filtres */}
        <section className="bg-white border-b sticky top-0 z-10 shadow-sm">
          <div className="container mx-auto px-4 py-4">
            <div className="flex items-center gap-2 overflow-x-auto pb-2">
              <Filter className="w-5 h-5 text-gray-500 flex-shrink-0" />
              <span className="text-sm font-medium text-gray-700 flex-shrink-0">Catégorie :</span>
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                    selectedCategory === cat
                      ? 'bg-[#FF6600] text-white shadow-md'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Grid des réalisations */}
        <section className="container mx-auto px-4 py-12">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[#FF6600] mb-4"></div>
              <p className="text-gray-600">Chargement des réalisations...</p>
            </div>
          ) : realisations.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-xl text-gray-600 mb-2">Aucune réalisation trouvée</p>
              <p className="text-gray-500">Essayez un autre filtre</p>
            </div>
          ) : (
            <>
              <div className="mb-6">
                <p className="text-gray-600">
                  <span className="font-semibold text-[#020d2a]">{realisations.length}</span> {realisations.length > 1 ? 'réalisations' : 'réalisation'}
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {realisations.map((realisation) => (
                  <a
                    key={realisation.id}
                    href={`/realisations/${realisation.slug}`}
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(`/realisations/${realisation.slug}`);
                    }}
                    className="bg-white rounded-xl shadow-md hover:shadow-2xl transition-all duration-300 cursor-pointer group overflow-hidden block no-underline"
                  >
                    {/* Image */}
                    <div className="relative h-64 overflow-hidden">
                      <img
                        src={realisation.main_image}
                        alt={realisation.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        loading="lazy"
                      />
                      <div className="absolute top-4 right-4">
                        <span className="bg-[#FF6600] text-white text-xs font-semibold px-3 py-1 rounded-full">
                          {realisation.category}
                        </span>
                      </div>
                    </div>

                    {/* Contenu */}
                    <div className="p-6">
                      <h2 className="text-xl font-bold text-[#020d2a] mb-2 group-hover:text-[#FF6600] transition-colors line-clamp-2">
                        {realisation.title}
                      </h2>
                      
                      <div className="flex items-center text-gray-600 text-sm mb-4">
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <span>{realisation.city}</span>
                        {realisation.date && (
                          <>
                            <span className="mx-2">•</span>
                            <span>{realisation.date}</span>
                          </>
                        )}
                      </div>

                      {realisation.equipments && realisation.equipments.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {realisation.equipments.slice(0, 3).map((equip, idx) => (
                            <span
                              key={idx}
                              className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded"
                            >
                              {equip}
                            </span>
                          ))}
                          {realisation.equipments.length > 3 && (
                            <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                              +{realisation.equipments.length - 3}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </a>
                ))}
              </div>
            </>
          )}
        </section>
      </main>

      <Footer />
    </>
  );
};

export default RealisationsListPage;
